import { z } from 'zod'

// Routes › Répartition (docs/routes/repartition/) : plan de la saison, lignes tracteur → opérateur prévu → routes
// ordonnées. Les schémas suivent les sorties réelles des fonctions `dispatch_*` (fixtures générées sur le banc PGlite,
// scripts/pglite-bench/scenarios/dispatch-outputs.sql). Les charges viennent toujours du serveur : le client ne
// recalcule jamais un trajet ni une durée de service.

export const lockModeSchema = z.enum(['FREE', 'ROUTE_LOCKED', 'POSITION_LOCKED'])
export type LockMode = z.infer<typeof lockModeSchema>

/** Catégorie de la route (`routes.category`), jamais déduite du nom (« 1 » / « A » reste un libellé). */
export const dispatchRouteCategorySchema = z.enum(['RESIDENTIAL', 'LARGE_SITE'])
export type DispatchRouteCategory = z.infer<typeof dispatchRouteCategorySchema>

export const stopKindSchema = z.enum(['RESIDENTIAL', 'COMMERCIAL', 'MULTI_UNIT'])
export type StopKind = z.infer<typeof stopKindSchema>

export const balanceSchema = z.enum(['BALANCED', 'ACCEPTABLE', 'UNBALANCED'])
export type Balance = z.infer<typeof balanceSchema>

const uuid = z.uuid()
const seconds = z.number().int().nonnegative()

export const serviceDurationSchema = z.object({
  category: stopKindSchema,
  defaultSeconds: seconds,
  observedSeconds: seconds.nullable(),
  samples: z.number().int().nonnegative(),
  usedSeconds: seconds,
  source: z.string(),
})
export type ServiceDuration = z.infer<typeof serviceDurationSchema>

export const dispatchSettingsSchema = z.object({
  isDefault: z.boolean().optional(),
  updatedAt: z.string().nullable().optional(),
  updatedByName: z.string().nullable().optional(),
  residentialServiceSeconds: seconds,
  commercialServiceSeconds: seconds,
  multiUnitServiceSeconds: seconds,
  balancedThresholdSeconds: seconds,
  acceptableThresholdSeconds: seconds,
  referenceStartTime: z.string(),
  travelSpeedKmh: z.number(),
  travelDetourFactor: z.number(),
  /** Lot E : temps automobile (parcours validé, Google) × ce facteur = temps tracteur. */
  roadDurationMultiple: z.number().optional(),
  serviceDurations: z.array(serviceDurationSchema),
})
export type DispatchSettings = z.infer<typeof dispatchSettingsSchema>

const loadSourcesSchema = z.object({ travel: z.string(), service: z.string(), transition: z.string() })
export type LoadSources = z.infer<typeof loadSourcesSchema>

export const routeLoadSchema = z.object({
  routeId: uuid,
  addressCount: z.number().int().nonnegative(),
  residentialCount: z.number().int().nonnegative(),
  commercialCount: z.number().int().nonnegative(),
  multiUnitCount: z.number().int().nonnegative(),
  unlocatedCount: z.number().int().nonnegative(),
  travelSeconds: seconds,
  serviceSeconds: seconds,
  totalSeconds: seconds,
  /** `ROUTE_PATH` (parcours validé à jour) ou `STRAIGHT_LINE_ESTIMATE`. */
  travelSource: z.string().optional(),
})
export type RouteLoad = z.infer<typeof routeLoadSchema>

export const lineLoadSchema = z.object({
  lineId: z.string().nullable(),
  tractorId: uuid,
  operatorId: uuid.nullable(),
  routeIds: z.array(uuid),
  routeCount: z.number().int().nonnegative(),
  travelSeconds: seconds,
  transitionSeconds: seconds,
  serviceSeconds: seconds,
  totalSeconds: seconds,
  estimatedFinishOffset: seconds,
  routes: z.array(
    z.object({
      routeId: uuid,
      transitionBeforeSeconds: seconds,
      transitionUnknown: z.boolean(),
      /** `GOOGLE_ROUTES`, `STRAIGHT_LINE_ESTIMATE`; nul pour la première route. */
      transitionSource: z.string().nullable().optional(),
    }),
  ),
  source: loadSourcesSchema,
})
export type LineLoad = z.infer<typeof lineLoadSchema>

export const planLoadSchema = z.object({
  routes: z.array(routeLoadSchema).optional(),
  lines: z.array(lineLoadSchema),
  summary: z.object({
    lastFinishSeconds: seconds,
    lastLineId: z.string().nullable(),
    spreadSeconds: seconds,
    activeLines: z.number().int().nonnegative(),
    balance: balanceSchema,
    referenceStartTime: z.string(),
  }),
  sources: loadSourcesSchema,
  settings: dispatchSettingsSchema,
})
export type PlanLoad = z.infer<typeof planLoadSchema>

// ------------------------------------------------------------------ Lot E : budget des appels Google

export const roadBudgetSchema = z.object({
  budget: z.literal('DISPATCH_ROAD_TIMES'),
  month: z.string(),
  monthlyUnitCap: z.number().int().nonnegative(),
  usedUnits: z.number().int().nonnegative(),
  usedCalls: z.number().int().nonnegative(),
  remainingUnits: z.number().int().nonnegative(),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
  canManage: z.boolean().optional(),
})
export type RoadBudget = z.infer<typeof roadBudgetSchema>

/** Estimation sans appel : éléments facturables (une paire chacun) et appels (un par point d'origine). */
export const roadTimesEstimateSchema = z.object({
  units: z.number().int().nonnegative(),
  calls: z.number().int().nonnegative(),
  budget: roadBudgetSchema,
})
export type RoadTimesEstimate = z.infer<typeof roadTimesEstimateSchema>

export const roadTimesResultSchema = roadTimesEstimateSchema.extend({
  status: z.literal('DONE'),
  stored: z.number().int().nonnegative(),
  missing: z.number().int(),
  replayed: z.boolean(),
})
export type RoadTimesResult = z.infer<typeof roadTimesResultSchema>

export const draftRouteSchema = z.object({
  routeId: uuid,
  rank: z.number().int().positive(),
  lockMode: lockModeSchema,
})

// ------------------------------------------------------------------ Lot F : proposition de plan de tracteurs

const proposalLoadSchema = z.object({
  summary: planLoadSchema.shape.summary,
  lines: z.array(lineLoadSchema),
  sources: loadSourcesSchema,
})

export const dispatchProposalSchema = z.object({
  runId: uuid,
  status: z.enum(['REVIEW', 'APPLIED', 'CANCELLED']),
  season: z.string(),
  planId: uuid,
  baseRevision: z.number().int().nonnegative(),
  /** Le brouillon a changé depuis le calcul : la proposition ne s'applique plus. */
  stale: z.boolean(),
  lines: z.array(
    z.object({
      lineId: uuid,
      equipmentId: uuid,
      operatorEmployeeId: uuid.nullable(),
      position: z.number().int().positive(),
      routes: z.array(draftRouteSchema),
    }),
  ),
  before: proposalLoadSchema,
  after: proposalLoadSchema,
  changes: z.object({
    routesMoved: z.number().int().nonnegative(),
    linesReordered: z.number().int().nonnegative(),
    gainSeconds: z.number().int(),
  }),
  parameters: z.object({ useRoadTimes: z.boolean().optional() }).loose(),
  usage: z.object({ provider: z.string().optional(), units: z.number().optional() }).loose(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
})
export type DispatchProposal = z.infer<typeof dispatchProposalSchema>

export const draftLineSchema = z.object({
  lineId: uuid,
  equipmentId: uuid,
  operatorEmployeeId: uuid.nullable(),
  position: z.number().int().positive(),
  routes: z.array(draftRouteSchema),
})
export type DraftLineRecord = z.infer<typeof draftLineSchema>

export const addressLockSchema = z.object({ serviceLocationId: uuid, lockMode: lockModeSchema })
export type AddressLock = z.infer<typeof addressLockSchema>

export const publishedLineSchema = z.object({
  lineId: uuid,
  equipmentId: uuid,
  equipmentLabel: z.string().nullable(),
  operatorEmployeeId: uuid.nullable(),
  operatorName: z.string().nullable(),
  position: z.number().int().positive(),
  routes: z.array(
    draftRouteSchema.extend({
      routeName: z.string().nullable(),
      routeColor: z.string().nullable(),
      routeCategory: dispatchRouteCategorySchema.nullable(),
    }),
  ),
})
export type PublishedLine = z.infer<typeof publishedLineSchema>

export const effectSchema = z.object({
  status: z.enum(['PENDING', 'APPLIED', 'SUPERSEDED', 'FAILED']),
  appliedAt: z.string().nullable(),
  errorCode: z.string().nullable(),
  deferredOperationId: z.string().nullable(),
})
export type DispatchEffect = z.infer<typeof effectSchema>

export const changeCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  routesAdded: z.number().int().nonnegative(),
  routesRemoved: z.number().int().nonnegative(),
  routesMoved: z.number().int().nonnegative(),
  linesReordered: z.number().int().nonnegative(),
  operatorsChanged: z.number().int().nonnegative(),
  locksChanged: z.number().int().nonnegative(),
  addressLocksChanged: z.number().int().nonnegative(),
})
export type ChangeCounts = z.infer<typeof changeCountsSchema>

const optionalText = z.string().nullable().optional()

/** Un changement du brouillon par rapport à la version publiée, calculé par le serveur. */
export const changeItemSchema = z.object({
  kind: z.string(),
  lineId: optionalText,
  routeId: optionalText,
  routeName: optionalText,
  equipmentId: optionalText,
  equipmentLabel: optionalText,
  before: optionalText,
  after: optionalText,
  beforeLabel: optionalText,
  afterLabel: optionalText,
  fromLineId: optionalText,
  toLineId: optionalText,
  fromLabel: optionalText,
  toLabel: optionalText,
  routeIds: z.array(z.string()).optional(),
  routeNames: z.array(z.string().nullable()).optional(),
  count: z.number().int().optional(),
})
export type ChangeItem = z.infer<typeof changeItemSchema>

export const planDiffSchema = z.object({
  hasChanges: z.boolean(),
  items: z.array(changeItemSchema),
  counts: changeCountsSchema,
})
export type PlanDiff = z.infer<typeof planDiffSchema>

export const operationBriefSchema = z
  .object({
    id: uuid,
    number: z.number().int(),
    status: z.string(),
    openedAt: z.string(),
    ageHours: z.number(),
    stale: z.boolean(),
  })
  .nullable()
export type OperationBrief = z.infer<typeof operationBriefSchema>

export const dispatchStopSchema = z.object({
  id: uuid,
  kind: stopKindSchema,
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
})
export type DispatchStop = z.infer<typeof dispatchStopSchema>

export const dispatchRouteSchema = z.object({
  id: uuid,
  name: z.string(),
  color: z.string().nullable(),
  category: dispatchRouteCategorySchema,
  sectorId: uuid.nullable(),
  displayOrder: z.number().int().nullable(),
  assignable: z.boolean(),
  stops: z.array(dispatchStopSchema),
})
export type DispatchRoute = z.infer<typeof dispatchRouteSchema>

export const dispatchWorkspaceSchema = z.object({
  season: z.string(),
  currentSeason: z.string(),
  plan: z
    .object({
      id: uuid,
      revision: z.number().int().nonnegative(),
      baseVersionId: uuid.nullable(),
      restoredFromVersion: z.number().int().nullable(),
      updatedAt: z.string().nullable(),
      updatedByName: z.string().nullable(),
    })
    .nullable(),
  draft: z.object({ lines: z.array(draftLineSchema), addressLocks: z.array(addressLockSchema) }),
  published: z
    .object({
      versionId: uuid,
      version: z.number().int().positive(),
      note: z.string().nullable(),
      publishedAt: z.string(),
      publishedByName: z.string().nullable(),
      lines: z.array(publishedLineSchema),
      addressLocks: z.array(addressLockSchema),
      effect: effectSchema.nullable(),
    })
    .nullable(),
  changes: planDiffSchema,
  routes: z.array(dispatchRouteSchema),
  equipments: z.array(
    z.object({
      id: uuid,
      label: z.string(),
      numero: z.string().nullable(),
      category: z.string().nullable(),
      available: z.boolean(),
    }),
  ),
  operators: z.array(
    z.object({ id: uuid, name: z.string(), eligible: z.boolean(), assignedEquipmentId: uuid.nullable() }),
  ),
  suggestedLines: z.array(
    z.object({
      equipmentId: uuid,
      operatorEmployeeId: uuid.nullable(),
      routes: z.array(z.object({ routeId: uuid, lockMode: lockModeSchema })),
    }),
  ),
  sectors: z.array(z.object({ id: uuid, name: z.string(), color: z.string().nullable() })),
  settings: dispatchSettingsSchema,
  load: z.object({ draft: planLoadSchema, published: planLoadSchema.nullable() }),
  operation: operationBriefSchema,
})
export type DispatchWorkspace = z.infer<typeof dispatchWorkspaceSchema>
export type DispatchEquipment = DispatchWorkspace['equipments'][number]
export type DispatchOperator = DispatchWorkspace['operators'][number]
export type DispatchSector = DispatchWorkspace['sectors'][number]
export type PublishedPlan = NonNullable<DispatchWorkspace['published']>

export const validationIssueSchema = z.object({
  code: z.string(),
  lineId: optionalText,
  routeId: optionalText,
  routeName: optionalText,
  equipmentLabel: optionalText,
  operatorName: optionalText,
  employeeId: optionalText,
  employeeName: optionalText,
  season: optionalText,
  currentSeason: optionalText,
  count: z.number().int().optional(),
  addressCount: z.number().int().optional(),
})
export type ValidationIssue = z.infer<typeof validationIssueSchema>

export const sectorGapSchema = z.object({
  routeId: uuid,
  routeName: z.string(),
  declaredSectorId: uuid,
  locations: z.number().int().nonnegative(),
  outside: z.number().int().nonnegative(),
  derived: z.array(z.object({ sectorId: uuid, locations: z.number().int().nonnegative() })),
})
export type SectorGap = z.infer<typeof sectorGapSchema>

export const publicationPreviewSchema = z.object({
  revision: z.number().int().nonnegative(),
  baseVersionId: uuid.nullable(),
  baseVersion: z.number().int().nullable(),
  nextVersion: z.number().int().positive(),
  changes: planDiffSchema,
  validation: z.object({
    blocking: z.array(validationIssueSchema),
    warnings: z.array(validationIssueSchema),
  }),
  /** Nul sans `sector.read` : l'écart de secteur n'est alors pas montré. */
  sectorGaps: z.array(sectorGapSchema).nullable(),
  operation: operationBriefSchema,
})
export type PublicationPreview = z.infer<typeof publicationPreviewSchema>

export const draftWriteResultSchema = z.object({
  planId: uuid,
  revision: z.number().int().nonnegative(),
  changed: z.boolean(),
})
export type DraftWriteResult = z.infer<typeof draftWriteResultSchema>

export const publishResultSchema = z.object({
  versionId: uuid,
  version: z.number().int().positive(),
  revision: z.number().int().nonnegative(),
  effect: z.enum(['APPLIED', 'PENDING']),
  operation: operationBriefSchema,
  changes: changeCountsSchema,
})
export type PublishResult = z.infer<typeof publishResultSchema>

export const historyVersionSchema = z.object({
  id: uuid,
  version: z.number().int().positive(),
  note: z.string().nullable(),
  publishedAt: z.string(),
  publishedByName: z.string().nullable(),
  restoredFromVersion: z.number().int().nullable(),
  lastFinishSeconds: seconds.nullable(),
  referenceStartTime: z.string().nullable(),
  changes: changeCountsSchema.nullable(),
  effect: effectSchema.nullable(),
})
export type HistoryVersion = z.infer<typeof historyVersionSchema>

export const historyEventSchema = z.object({
  id: z.number().int(),
  kind: z.string(),
  details: z.record(z.string(), z.unknown()).nullable(),
  version: z.number().int().nullable(),
  revision: z.number().int().nullable(),
  actorName: z.string().nullable(),
  occurredAt: z.string(),
})
export type HistoryEvent = z.infer<typeof historyEventSchema>

export const dispatchHistorySchema = z.object({
  versions: z.array(historyVersionSchema),
  events: z.array(historyEventSchema),
})
export type DispatchHistory = z.infer<typeof dispatchHistorySchema>

export const versionContentSchema = z.object({
  id: uuid,
  season: z.string(),
  version: z.number().int().positive(),
  note: z.string().nullable(),
  publishedAt: z.string(),
  publishedByName: z.string().nullable(),
  lines: z.array(publishedLineSchema),
  addressLocks: z.array(addressLockSchema),
  load: planLoadSchema,
  loadAtPublication: planLoadSchema.nullable(),
})
export type VersionContent = z.infer<typeof versionContentSchema>

// ------------------------------------------------------------------ Commandes

export interface EditableRoute {
  routeId: string
  lockMode: LockMode
}

/** Ligne du brouillon telle que l'écran l'édite; `lineId` nul pour une ligne pas encore enregistrée. */
export interface EditableLine {
  lineId: string | null
  equipmentId: string
  operatorEmployeeId: string | null
  routes: EditableRoute[]
}

export interface SaveDraftCommand {
  season: string
  expectedRevision: number
  lines: EditableLine[]
  addressLocks?: AddressLock[]
  source?: 'MANUAL' | 'PROPOSAL'
}

export interface RevisionCommand {
  season: string
  expectedRevision: number
}

export interface RestoreVersionCommand extends RevisionCommand {
  versionId: string
}

export interface PublishCommand extends RevisionCommand {
  baseVersionId: string | null
  note: string
}

export interface SettingsCommand {
  residentialServiceSeconds: number
  commercialServiceSeconds: number
  multiUnitServiceSeconds: number
  balancedThresholdSeconds: number
  acceptableThresholdSeconds: number
  referenceStartTime: string
  roadDurationMultiple?: number
}

export interface OptimizeCommand {
  season: string
  /** Matrice Google Routes avant le calcul (facturable, plafonnée); sinon cache et vol d'oiseau. */
  useRoadTimes: boolean
}

// ------------------------------------------------------------------ Édition locale du brouillon

export function editableLines(lines: readonly DraftLineRecord[]): EditableLine[] {
  return [...lines]
    .sort((a, b) => a.position - b.position)
    .map((line) => ({
      lineId: line.lineId,
      equipmentId: line.equipmentId,
      operatorEmployeeId: line.operatorEmployeeId,
      routes: [...line.routes]
        .sort((a, b) => a.rank - b.rank)
        .map((route) => ({ routeId: route.routeId, lockMode: route.lockMode })),
    }))
}

export type MoveRefusal = 'POSITION_LOCKED' | 'ROUTE_LOCKED'

export type EditResult =
  { ok: true; lines: EditableLine[] } | { ok: false; refusal: MoveRefusal; routeId: string }

function locate(lines: readonly EditableLine[], routeId: string): { line: number; index: number } | null {
  for (let line = 0; line < lines.length; line += 1) {
    const index = lines[line]?.routes.findIndex((route) => route.routeId === routeId) ?? -1
    if (index >= 0) return { line, index }
  }
  return null
}

/**
 * Le verrou que le serveur appliquera (`DISPATCH_ROUTE_LOCKED`) : une route « reste sur ce tracteur » ne change pas
 * de ligne; une route « ne pas déplacer » garde sa ligne et son rang, y compris quand une autre route est insérée ou
 * retirée avant elle.
 */
export function lockViolation(
  before: readonly EditableLine[],
  after: readonly EditableLine[],
): EditResult | null {
  for (const [lineIndex, line] of before.entries()) {
    for (const [index, route] of line.routes.entries()) {
      if (route.lockMode === 'FREE') continue
      const now = locate(after, route.routeId)
      const afterLock = now ? after[now.line]?.routes[now.index]?.lockMode : undefined
      // Le verrou change dans la même commande : c'est la nouvelle règle qui s'applique.
      if (afterLock !== route.lockMode) continue
      const sameLine = now !== null && now.line === lineIndex
      if (route.lockMode === 'ROUTE_LOCKED' && !sameLine)
        return { ok: false, refusal: 'ROUTE_LOCKED', routeId: route.routeId }
      if (route.lockMode === 'POSITION_LOCKED' && !(sameLine && now.index === index))
        return { ok: false, refusal: 'POSITION_LOCKED', routeId: route.routeId }
    }
  }
  return null
}

function cloneLines(lines: readonly EditableLine[]): EditableLine[] {
  return lines.map((line) => ({ ...line, routes: line.routes.map((route) => ({ ...route })) }))
}

/**
 * Déplace une route vers une ligne (`toLine` = index de ligne, `null` = hors plan) à la position `toIndex` (index
 * d'insertion dans la ligne d'arrivée, compté avant le retrait). Refusé si un verrou serait enfreint.
 */
export function moveRoute(
  lines: readonly EditableLine[],
  routeId: string,
  toLine: number | null,
  toIndex: number,
  lockMode: LockMode = 'FREE',
): EditResult {
  const next = cloneLines(lines)
  const from = locate(next, routeId)
  let moved: EditableRoute = { routeId, lockMode }
  if (from) {
    const [removed] = next[from.line]?.routes.splice(from.index, 1) ?? []
    if (removed) moved = removed
  }
  if (toLine !== null) {
    const target = next[toLine]
    if (!target) return { ok: true, lines: cloneLines(lines) }
    let index = toIndex
    if (from?.line === toLine && from.index < toIndex) index -= 1
    target.routes.splice(Math.max(0, Math.min(index, target.routes.length)), 0, moved)
  }
  return lockViolation(lines, next) ?? { ok: true, lines: next }
}

export function setRouteLock(
  lines: readonly EditableLine[],
  routeId: string,
  lockMode: LockMode,
): EditableLine[] {
  return lines.map((line) => ({
    ...line,
    routes: line.routes.map((route) => (route.routeId === routeId ? { ...route, lockMode } : { ...route })),
  }))
}

/** Un opérateur ne conduit qu'un tracteur à la fois : choisi ici, il quitte l'autre ligne. */
export function setLineOperator(
  lines: readonly EditableLine[],
  lineIndex: number,
  operatorEmployeeId: string | null,
): EditableLine[] {
  return lines.map((line, index) => {
    if (index === lineIndex)
      return { ...line, operatorEmployeeId, routes: line.routes.map((r) => ({ ...r })) }
    if (operatorEmployeeId !== null && line.operatorEmployeeId === operatorEmployeeId)
      return { ...line, operatorEmployeeId: null, routes: line.routes.map((r) => ({ ...r })) }
    return { ...line, routes: line.routes.map((r) => ({ ...r })) }
  })
}

export function addLine(lines: readonly EditableLine[], equipmentId: string): EditableLine[] {
  return [...cloneLines(lines), { lineId: null, equipmentId, operatorEmployeeId: null, routes: [] }]
}

/** Seule une ligne sans route se retire : ses routes doivent d'abord changer de tracteur. */
export function removeLine(lines: readonly EditableLine[], lineIndex: number): EditableLine[] {
  if ((lines[lineIndex]?.routes.length ?? 0) > 0) return cloneLines(lines)
  return cloneLines(lines).filter((_, index) => index !== lineIndex)
}

export function sameLines(a: readonly EditableLine[], b: readonly EditableLine[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

// ------------------------------------------------------------------ Présentation des durées

/** « 42 min », « 1 h 05 » : durée lisible en minutes arrondies. */
export function formatDuration(totalSeconds: number): string {
  const minutes = Math.round(totalSeconds / 60)
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return hours > 0 ? `${String(hours)} h ${String(rest).padStart(2, '0')}` : `${String(rest)} min`
}

/** « +12 min », « −1 h 05 », « ±0 min ». */
export function formatSignedDuration(deltaSeconds: number): string {
  const sign = deltaSeconds > 0 ? '+' : deltaSeconds < 0 ? '−' : '±'
  return `${sign}${formatDuration(Math.abs(deltaSeconds))}`
}

/** Heure de fin : heure de départ de référence (« 02:00 ») plus la charge, modulo 24 h. */
export function formatFinish(referenceStartTime: string, offsetSeconds: number): string {
  const [hours, minutes] = referenceStartTime.split(':').map((part) => Number.parseInt(part, 10))
  const start =
    (Number.isFinite(hours) ? (hours ?? 0) : 0) * 60 + (Number.isFinite(minutes) ? (minutes ?? 0) : 0)
  const total = start + Math.round(offsetSeconds / 60)
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

export const balanceLabel: Record<Balance, string> = {
  BALANCED: 'Équilibré',
  ACCEPTABLE: 'Acceptable',
  UNBALANCED: 'Déséquilibré',
}

export const lockLabel: Record<LockMode, string> = {
  FREE: 'Déplaçable',
  ROUTE_LOCKED: 'Reste sur ce tracteur',
  POSITION_LOCKED: 'Ne pas déplacer',
}

export const lockHelp: Record<LockMode, string> = {
  FREE: 'La route peut changer de tracteur et de position.',
  ROUTE_LOCKED: 'La route reste sur ce tracteur. Son ordre peut changer.',
  POSITION_LOCKED: 'Tracteur et position figés. Personne ne peut la déplacer sans retirer le verrou.',
}

export const categoryLabel: Record<DispatchRouteCategory, string> = {
  RESIDENTIAL: 'Résidentiel',
  LARGE_SITE: 'Commercial / multilogements',
}

export const stopKindLabel: Record<StopKind, string> = {
  RESIDENTIAL: 'Résidence',
  COMMERCIAL: 'Commerce',
  MULTI_UNIT: 'Multilogements',
}

/** Libellé d'un changement calculé par le serveur : type, portée et détail. */
export function describeChange(item: ChangeItem): { kind: string; scope: string; text: string } {
  const label = (value: string | null | undefined, fallback = '—') => value ?? fallback
  switch (item.kind) {
    case 'LINE_ADDED':
      return { kind: 'Tracteur', scope: label(item.equipmentLabel), text: 'Ajouté au plan' }
    case 'LINE_REMOVED':
      return { kind: 'Tracteur', scope: label(item.equipmentLabel), text: 'Retiré du plan' }
    case 'EQUIPMENT_CHANGED':
      return {
        kind: 'Tracteur',
        scope: 'Équipement',
        text: `${label(item.beforeLabel)} → ${label(item.afterLabel)}`,
      }
    case 'OPERATOR_CHANGED':
      return {
        kind: 'Opérateur',
        scope: label(item.equipmentLabel),
        text: `${label(item.beforeLabel, 'Non assigné')} → ${label(item.afterLabel, 'Non assigné')}`,
      }
    case 'ROUTE_ADDED':
      return { kind: 'Route', scope: label(item.routeName), text: `Ajoutée au ${label(item.equipmentLabel)}` }
    case 'ROUTE_REMOVED':
      return { kind: 'Route', scope: label(item.routeName), text: `Retirée du ${label(item.equipmentLabel)}` }
    case 'ROUTE_MOVED':
      return {
        kind: 'Tracteur',
        scope: label(item.routeName),
        text: `${label(item.fromLabel)} → ${label(item.toLabel)}`,
      }
    case 'ROUTE_REORDERED':
      return {
        kind: 'Ordre',
        scope: label(item.equipmentLabel),
        text: (item.routeNames ?? []).map((name) => name ?? '—').join(' → '),
      }
    case 'ROUTE_LOCK_CHANGED': {
      const before = lockModeSchema.safeParse(item.before)
      const after = lockModeSchema.safeParse(item.after)
      return {
        kind: 'Verrouillage',
        scope: label(item.routeName),
        text: `${before.success ? lockLabel[before.data] : '—'} → ${after.success ? lockLabel[after.data] : '—'}`,
      }
    }
    case 'ADDRESS_LOCKS_CHANGED':
      return { kind: 'Adresses', scope: 'Verrous d’adresses', text: `${String(item.count ?? 0)} modifié(s)` }
    default:
      return { kind: 'Changement', scope: label(item.routeName ?? item.equipmentLabel), text: item.kind }
  }
}

/** Marque d'une route sur la carte et dans sa ligne : déplacée, ordre, verrou, ajoutée. */
export function routeMarks(changes: PlanDiff): Map<string, string> {
  const marks = new Map<string, string>()
  for (const item of changes.items) {
    if (item.kind === 'ROUTE_MOVED' && item.routeId) marks.set(item.routeId, 'Déplacée')
    if (item.kind === 'ROUTE_ADDED' && item.routeId && !marks.has(item.routeId))
      marks.set(item.routeId, 'Ajoutée')
    if (item.kind === 'ROUTE_LOCK_CHANGED' && item.routeId && !marks.has(item.routeId))
      marks.set(item.routeId, 'Verrou')
  }
  for (const item of changes.items) {
    if (item.kind !== 'ROUTE_REORDERED') continue
    for (const routeId of item.routeIds ?? []) if (!marks.has(routeId)) marks.set(routeId, 'Ordre')
  }
  return marks
}

const blockingText: Record<string, (issue: ValidationIssue) => string> = {
  SEASON_NOT_CURRENT: (issue) => `La saison ${issue.season ?? ''} n’est pas la saison courante.`,
  PLAN_EMPTY: () => 'Le plan ne contient aucune route.',
  LINE_WITHOUT_OPERATOR: (issue) =>
    `${issue.equipmentLabel ?? 'Un tracteur'} a des routes sans opérateur prévu.`,
  OPERATOR_NOT_ELIGIBLE: (issue) => `${issue.operatorName ?? 'Un opérateur'} n’est plus un opérateur actif.`,
  EQUIPMENT_UNAVAILABLE: (issue) => `${issue.equipmentLabel ?? 'Un tracteur'} n’est pas disponible.`,
  ROUTE_UNAVAILABLE: (issue) => `${issue.routeName ?? 'Une route'} n’est plus affectable.`,
  LINE_WITHOUT_ROUTES: (issue) =>
    `${issue.equipmentLabel ?? 'Un tracteur'} n’a aucune route (${issue.operatorName ?? 'sans opérateur'}).`,
  ROUTE_NOT_IN_PLAN: (issue) =>
    `${issue.routeName ?? 'Une route'} (${String(issue.addressCount ?? 0)} adresses) n’est dans aucun tracteur.`,
  ROUTE_TAKEN_FROM_EMPLOYEE: (issue) =>
    `${issue.routeName ?? 'Une route'} sera retirée de la liste de ${issue.employeeName ?? 'un employé'}.`,
  UNLOCATED_ADDRESSES: (issue) =>
    `${issue.routeName ?? 'Une route'} : ${String(issue.count ?? 0)} adresse(s) sans position, trajet sous-estimé.`,
}

export function describeIssue(issue: ValidationIssue): string {
  return blockingText[issue.code]?.(issue) ?? issue.code
}
